import { getCookie } from 'helpers/cookie';
import { appendOrAddQueryParam } from 'helpers/queryParam';
import type { ICollectionState } from 'store/slices/collection';
import { getTranslationKey } from './texting';

interface IIds {
  selection_id?: string;
  gallery_id?: string;
  gallery_ids?: string[];
  image_id?: string;
  collection_id?: string;
}

interface ISettings {
  collection: ICollectionState;
  environment: any;
  quality?: any;
}

interface IReturnUrl {
  error?: any;
  url?: string;
  infoModalText: string;
  infoModalText2: string;
}

// The ZIP is built before the server answers, so mobile users see nothing for a while — tell them
export const getDownloadInfoTexts = (environment: any) => {
  const { isMobileDevice, isMobileSafari, isIDevice, iosVersion, isApp, isMobileChrome } =
    environment;
  const isIos13 = !!(iosVersion && iosVersion >= 13);

  let infoModalText;
  let infoModalText2;

  if (isMobileDevice) {
    if (isApp) {
      if (isMobileChrome) {
        infoModalText = getTranslationKey('downloadStarted');
        // a standalone iOS PWA has no "Safari" in its user agent, so check the device instead
      } else if (isIDevice && isIos13) {
        infoModalText = getTranslationKey('downloadStartedIosApp');
      }
    } else if (isMobileSafari && isIos13) {
      infoModalText = getTranslationKey('downloadStartedIos');
    }

    infoModalText2 = getTranslationKey('bigData');
  }

  return { infoModalText, infoModalText2 };
};

export const getDownloadUrl = (
  { selection_id, gallery_id, gallery_ids, image_id, collection_id }: IIds,
  { collection, environment, quality }: ISettings
): IReturnUrl => {
  try {
    const { downloadPin, _id: id, password, downloadToken } = collection;
    const { iosVersion, isApp, isTest } = environment;

    let apiURLDL = window.API_URL_DL;

    if (isApp && iosVersion && iosVersion >= 13) {
      if (password && !downloadToken) {
        return {
          error: 'buggyDownloadIosApp',
          infoModalText: getTranslationKey('buggyDownloadIosApp'),
          infoModalText2: window.location.href
        };
      }
      if (isTest) {
        apiURLDL = 'https://api-dev.galleryapps.io';
      }
    }

    let url = '';
    // only these two routes resolve an endCustomer server-side; the rest ignore the token
    let needsEndCustomer = false;

    if (selection_id) {
      url = `${apiURLDL}/api/download/collection/${id}/selection/${selection_id}`;
      needsEndCustomer = true;
    } else if (gallery_ids) {
      url = `${apiURLDL}/api/download/collection/${id}/galleries`;
    } else if (gallery_id) {
      url = `${apiURLDL}/api/download/collection/${id}/gallery/${gallery_id}`;
    } else if (image_id) {
      url = `${apiURLDL}/api/download/image/${id}/${image_id}`;
    } else if (collection_id) {
      url = `${apiURLDL}/api/download/collection/${id}`;
      needsEndCustomer = true;
    } else {
      throw 'No idetenifier set getDownloadUrl()';
    }

    const endCustomerToken = getCookie('endCustomerToken') || undefined;

    const queryParams: Record<string, string | undefined> = {
      q: quality || 'high',
      downloadToken,
      endCustomerToken: needsEndCustomer ? endCustomerToken : undefined
    };
    if (gallery_ids) {
      queryParams.gallery_ids = gallery_ids.join(',');
    }
    if (downloadPin) {
      queryParams.downloadPin = downloadPin;
    }

    url = appendOrAddQueryParam({
      url,
      params: queryParams
    });

    return {
      url,
      ...getDownloadInfoTexts(environment)
    };
  } catch (e) {
    console.log(e);

    // @ts-ignore
    return { error: e };
  }
};
